import moment from 'moment'
import { Button, Buttons, Panel, PanelTitle, StatusBadge } from 'components/atoms'
import SvgIcon from 'components/atoms/SvgIcon'

import LinkButton from 'components/atoms/LinkButton'
import StatusTip, { ArrowStatus } from 'components/atoms/StatusTip'
import { EditTable } from 'components/organisms'
import MessagePreviews from 'components/organisms/Dashboard/MessagesPanelBody'
import DashboardProfile from 'components/organisms/Dashboard/Profile'
import Summary from 'components/organisms/Dashboard/Summary'
import I18n from 'core/i18n'
import { IJsonResponse } from '../../../core/JsonApiSerializer'
import { utilService, reservationService } from 'core/services'
import * as React from 'react'
import { BREAKPOINT_DESKTOP, BREAKPOINT_TABLET_LARGE, COLORS } from 'static/constants'
import { IUser, IWindow, ISales } from 'static/interfaces'
import styled from 'styled-components'
import SidebarLayout from '../_layouts/sidebarLayout'
import { showDeclineModal } from '../reservations/parts/DeclineModal'

declare var window: IWindow

interface IProps {
  user: IUser
  agent_member?: { name: string; email: string }
  sales_data: ISales
  messages: IJsonResponse
  reservations: IJsonResponse
  host_cancel_reasons: IJsonResponse
}

const COLUMNS = [
  {
    name: I18n.t('generic.status'),
    field: 'status',
    required: false,
    readOnly: true,
    renderElement: record => <StatusBadge label={record.workflow_state} type="info" />,
  },
  {
    name: `${I18n.t('reservation.reservation_no')}/${I18n.t('generic.guest')}`,
    field: 'id',
    required: false,
    readOnly: true,
    renderElement: record => <>{`${record.no}/${record.user?.username}`}</>,
  },
  {
    name: I18n.t('post.post_name'),
    field: 'tour',
    required: false,
    renderElement: record => <>{record.post?.name}</>,
  },
  {
    name: I18n.t('generic.tour_schedule'),
    field: 'schedule',
    required: false,
    renderElement: record => (
      <>
        {record.start_at} - {record.end_at}
      </>
    ),
  },
  {
    name: I18n.t('guide_report.num_of_people'),
    field: 'num_of_people',
    required: false,
    renderElement: record => (
      <>
        <p>
          {I18n.t('generic.adult')}：{record.num_of_adults}
        </p>
        <p>
          {I18n.t('generic.child')}：{record.num_of_children}
        </p>
        <p>
          {I18n.t('generic.infant')}：{record.num_of_infants}
        </p>
      </>
    ),
  },
]

const DashboardPanelHeader: React.VFC<{ title: string; indexLink: string }> = ({
  title,
  indexLink,
}) => {
  return (
    <PanelTitle
      title={title}
      rightContent={
        <LinkButton href={indexLink}>
          <>
            {I18n.t('generic.show_index')}{' '}
            <SvgIcon name="arrow_forward" className="icon" ariaHidden={true} role="presentation" />
          </>
        </LinkButton>
      }
    />
  )
}

const Dashboard: React.VFC<IProps> = ({
  user,
  agent_member,
  sales_data,
  messages,
  reservations,
  host_cancel_reasons,
}) => {
  const { data: _messages } = utilService.getDataFromJson(messages)
  const { data: _reservations } = utilService.getDataFromJson(reservations)
  const { data: userData } = utilService.getDataFromJson(user)
  const { data: hostCancelReasons } = utilService.getDataFromJson(host_cancel_reasons)
  const [records, setRecords] = React.useState(_reservations)
  const formRef = React.useRef(null)

  const amountDiff = sales_data.amount - sales_data.amount_last_month
  const status: ArrowStatus = React.useMemo(() => {
    if (amountDiff === 0) {
      return 'flat'
    }
    if (amountDiff < 0) {
      return 'down'
    }
    return 'up'
  }, [amountDiff])
  const unit = React.useMemo(() => {
    if (status === 'up') {
      return '+'
    }
    if (status === 'down') {
      return '-'
    }
    return ''
  }, [status])
  const approveReservation = async id => {
    const { reservation: changedReservation, flush } = await reservationService.approveReservation(
      id
    )
    setRecords(
      records.map(record => {
        return record.id === id ? changedReservation : record
      })
    )
    window.flashMessages.addMessage({ text: flush.message, type: flush.type })
  }
  const handleDecline = (id, reservation, flush) => {
    setRecords(
      records.map(record => {
        return record.id === id ? reservation : record
      })
    )
    window.flashMessages.addMessage({ text: flush.message, type: flush.type })
  }
  const renderRecordControls = React.useCallback(
    record => (
      <Buttons inTable={true}>
        <LinkButton
          miniLink={true}
          fullWidth={true}
          showBorder={true}
          href={`/conversations/${record.conversation?.slug}`}
        >
          {I18n.t('generic.message')}
        </LinkButton>
        {record.workflow_state_enum_value === 'requested' && (
          <Buttons inline={true}>
            <Button
              rounded={true}
              small={true}
              textColor={COLORS.caution}
              borderColor={COLORS.caution}
              handleClick={() => {
                showDeclineModal(userData, record.id, handleDecline, formRef, hostCancelReasons)
              }}
            >
              {I18n.t('generic.decline')}
            </Button>
            <Button rounded={true} small={true} handleClick={() => approveReservation(record.id)}>
              {I18n.t('generic.approve')}
            </Button>
          </Buttons>
        )}
      </Buttons>
    ),
    [records]
  )
  return (
    <SidebarLayout user={userData}>
      <DashboardContent>
        <Panel className="full" marginType="dashboard">
          <DashboardProfile user={userData} agentMember={agent_member} />
          <DashboardSummary>
            <Summary data={sales_data.rates} />
            <div className="buttons">
              <LinkButton href="/agent/sales" miniLink={true} showBorder={true}>
                {I18n.t('sales.title')}
              </LinkButton>
            </div>
          </DashboardSummary>
        </Panel>

        <Panel
          title={
            <DashboardPanelHeader title={I18n.t('generic.message')} indexLink="/conversations" />
          }
          marginType="dashboard"
        >
          <MessagePreviews messages={_messages} />
        </Panel>
        <Panel
          title={<DashboardPanelHeader title={I18n.t('sales.title')} indexLink="/agent/sales" />}
          marginType="dashboard"
        >
          <Management>
            <StatusTip status={status} />
            <div className="management_summary">
              <div className="date">{moment().format('YYYY/MM')}</div>
              <div className="price">¥{sales_data.amount}</div>
              <div className="transition">
                <span className="date">先月</span>
                <span className={unit}>
                  {unit} ¥{Math.abs(amountDiff)}
                </span>
              </div>
            </div>
          </Management>
        </Panel>
        <Panel
          title={
            <DashboardPanelHeader
              title={I18n.t('post.latest_reservation')}
              indexLink="/agent/host_reservations"
            />
          }
          className="full"
          marginType="dashboard"
        >
          <EditTable
            columns={COLUMNS}
            records={records}
            editable={true}
            renderRecordControls={renderRecordControls}
          />
        </Panel>
      </DashboardContent>
    </SidebarLayout>
  )
}

const DashboardSummary = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-evenly;
  width: 100%;
  .buttons {
    flex-shrink: 0;
  }
  @media (max-width: ${BREAKPOINT_TABLET_LARGE}px) {
    flex-direction: column;
    .buttons {
      margin-top: 32px;
    }
  }
`

const DashboardContent = styled.div`
  display: grid;
  grid-template-columns: 620fr 440fr;
  column-gap: 20px;
  row-gap: 20px;
  > .full {
    grid-column-start: 1;
    grid-column-end: 3;
  }
  @media (max-width: ${BREAKPOINT_DESKTOP}px) {
    display: flex;
    flex-direction: column;
  }
`

const Management = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  @media (max-width: ${BREAKPOINT_DESKTOP}px) {
    justify-content: center;
  }

  .management_summary {
    display: flex;
    flex-direction: column;
    align-items: center;
    font-weight: 700;
    margin-left: 24px;
    @media (max-width: ${BREAKPOINT_TABLET_LARGE}px) {
      margin-left: 16px;
    }
    .date {
      display: flex;
      font-size: 12px;
      color: ${COLORS.subText};
    }
    .price {
      font-style: normal;
      font-size: 28px;
    }
    .transition {
      display: flex;
      align-items: center;
      .date {
        margin-right: 10px;
      }
      .plus {
        color: ${COLORS.successText};
      }
      .flat {
        color: ${COLORS.useallyText};
      }
      .minus {
        color: ${COLORS.cautionText};
      }
    }
  }
`

export default Dashboard
